const MAX_ROUTE_DECODE_ROUNDS = 3;
const REDACTED_ROUTE = "/:redacted";
const ENCODED_STRUCTURAL_DELIMITER = /%(?:25)*(?:2f|3f|23)/i;
const MULTIPLE_LEADING_SLASHES = /^\/{2,}/;

const ROUTE_TEMPLATES: ReadonlyArray<readonly [RegExp, string]> = [
  [/^\/invite\/[^/]+$/, "/invite/:token"],
  [/^\/join\/[^/]+$/, "/join/:token"],
  [/^\/nudge\/[^/]+$/, "/nudge/:id"],
  [/^\/assess\/[^/]+$/, "/assess/:meetingId"],
  [/^\/reflect\/[^/]+$/, "/reflect/:meetingId"],
  [/^\/meetings\/[^/]+$/, "/meetings/:meetingId"],
  [/^\/journey\/summary\/[^/]+$/, "/journey/summary/:summaryId"],
  [/^\/transcript-analysis\/[^/]+$/, "/transcript-analysis/:transcriptId"],
  [/^\/post-meeting\/[^/]+$/, "/post-meeting/:transcriptId"],
  [/^\/coach\/leaders\/[^/]+\/synthesis\/[^/]+$/, "/coach/leaders/:leaderId/synthesis/:behaviorId"],
  [/^\/coach\/leaders\/[^/]+\/record$/, "/coach/leaders/:leaderId/record"],
  [/^\/coach\/leaders\/[^/]+$/, "/coach/leaders/:leaderId"],
  [/^\/coach\/review\/sequence\/[^/]+$/, "/coach/review/sequence/:leaderId"],
  [/^\/coach\/review\/[^/]+\/[^/]+$/, "/coach/review/:type/:leaderId"],
  [/^\/coach\/post-meeting\/[^/]+$/, "/coach/post-meeting/:transcriptId"],
  [/^\/coach\/reflect\/[^/]+$/, "/coach/reflect/:meetingId"],
  [/^\/coach\/meetings\/[^/]+\/reflect$/, "/coach/meetings/:meetingId/reflect"],
  [/^\/coach\/meetings\/[^/]+$/, "/coach/meetings/:meetingId"],
];

function normalizeRoute(pathname: string) {
  return pathname.split(/[?#]/)[0].replace(/\/+$/, "") || "/";
}

function matchRouteTemplate(route: string) {
  const matchableRoute = MULTIPLE_LEADING_SLASHES.test(route)
    ? route.replace(MULTIPLE_LEADING_SLASHES, "/")
    : route;

  for (const [pattern, template] of ROUTE_TEMPLATES) {
    if (pattern.test(matchableRoute)) return template;
  }
  return null;
}

function decodeRouteOnce(route: string) {
  try {
    return decodeURIComponent(route);
  } catch {
    return route;
  }
}

function sanitizePathname(pathname: string) {
  let route = normalizeRoute(pathname);

  for (let round = 0; round <= MAX_ROUTE_DECODE_ROUNDS; round += 1) {
    if (/^https?:\/\//i.test(route)) return REDACTED_ROUTE;
    const template = matchRouteTemplate(route);
    if (template) return template;
    if (round === MAX_ROUTE_DECODE_ROUNDS) break;

    const decodedRoute = normalizeRoute(decodeRouteOnce(route));
    if (decodedRoute === route) break;
    route = decodedRoute;
  }

  return MULTIPLE_LEADING_SLASHES.test(route) || ENCODED_STRUCTURAL_DELIMITER.test(route)
    ? REDACTED_ROUTE
    : route;
}

export function sanitizeAppRoute(value: string) {
  const input = value.trim();

  if (/^https?:\/\//i.test(input)) {
    try {
      const url = new URL(input);
      url.pathname = sanitizePathname(url.pathname);
      url.username = "";
      url.password = "";
      url.search = "";
      url.hash = "";
      return url.toString();
    } catch {
      return sanitizePathname(input);
    }
  }

  return sanitizePathname(input);
}
